import apiClient from '@/api/client'

/**
 * A workspace partitions lead data — AR Tech Solutions' own book versus each B2B
 * partner's. One workspace record serves Leads, Contacts, Organizations and
 * Deals alike, so this is the only client any of those pages needs.
 *
 * The tenant's own workspace is `workspaceType: 'internal'`; partners are
 * 'partner'. Adding a partner is a POST here, not a code change.
 */
export interface Workspace {
  id: string
  tenantId: string
  name: string
  code: string
  workspaceType: 'internal' | 'partner'
  status: 'active' | 'inactive'
  description: string | null
  color: string | null
  /** Records owned across all four modules. Only present with withCounts. */
  recordCount: number | null
  createdAt: string | null
  updatedAt: string | null
}

export interface CreateWorkspaceData {
  name: string
  code: string
  workspaceType?: 'internal' | 'partner'
  status?: 'active' | 'inactive'
  description?: string
  color?: string
}

export interface UpdateWorkspaceData extends Partial<CreateWorkspaceData> {}

export const workspacesApi = {
  /**
   * Every workspace in the account. Guarded server-side by leads:READ rather
   * than an admin permission, so a sales rep's picker populates without giving
   * them the admin directory.
   */
  getWorkspaces: async (
    opts?: { includeInactive?: boolean; withCounts?: boolean },
  ): Promise<Workspace[]> => {
    const res = await apiClient.get<Workspace[]>('/workspaces', {
      params: {
        includeInactive: opts?.includeInactive ?? true,
        withCounts: opts?.withCounts ?? false,
      },
    })
    return (res as any).data ?? []
  },

  createWorkspace: async (data: CreateWorkspaceData): Promise<Workspace> => {
    const res = await apiClient.post<Workspace>('/workspaces', data)
    return (res as any).data
  },

  updateWorkspace: async (id: string, data: UpdateWorkspaceData): Promise<Workspace> => {
    const res = await apiClient.put<Workspace>(`/workspaces/${id}`, data)
    return (res as any).data
  },

  /**
   * Only succeeds on an empty workspace — the API answers 409 while any record
   * still references it, so a partner's book can never be orphaned. Retiring a
   * partner is an update to status: 'inactive' instead.
   *
   * `reason` is required and audited, like deleting a lead. It travels in the
   * query string, not a body: some proxies drop DELETE bodies and the call then
   * fails silently.
   */
  deleteWorkspace: async (id: string, reason: string): Promise<void> => {
    await apiClient.delete(`/workspaces/${id}`, { params: { reason } })
  },
}
